import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import RaceSetupForm from "@/components/race/RaceSetupForm";
import { toast } from "sonner";

// Race setup — build a new race plan, or edit an existing one from step 1.
export default function RacePackSetup() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const editing = !!planId;

  useEffect(() => {
    if (!planId) return;
    api.get(`/race-pack/plans/${planId}`)
      .then(({ data }) => setPlan(data))
      .catch((e) => {
        toast.error(apiError(e, "Could not open that race plan."));
        navigate("/race-pack");
      });
  }, [planId, navigate]);

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/race-pack/plans/${planId}`, payload);
        toast.success("Race plan updated.");
        navigate(`/race-pack/${planId}`);
      } else {
        const { data } = await api.post("/race-pack/plans", payload);
        toast.success("Race plan built.");
        navigate(`/race-pack/${data.plan.id}`);
      }
    } catch (e) {
      toast.error(apiError(e, "Could not save your race plan."));
      setSaving(false);
    }
  };

  if (editing && !plan) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="race-setup-page">
      <header className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <Link to="/race-pack" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <span className="font-display text-base uppercase tracking-tight">
              <span className="text-[#E2E30A]">Race</span> Setup
            </span>
          </Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 sm:px-8 pt-10">
        {editing && (
          <p className="text-sm text-zinc-400 mb-6" data-testid="race-setup-editing">
            Editing “{plan.name}” — change anything and save. No race credit is used.
          </p>
        )}
        <RaceSetupForm initialPlan={plan} onSubmit={submit} saving={saving}
          submitLabel={editing ? "Save changes" : "Build race plan"}
          onCancel={() => navigate(editing ? `/race-pack/${planId}` : "/race-pack")} />
      </main>
    </div>
  );
}
